/**
 * MatchKeyStepContent — shared run-intent step.
 *
 * Renders the Match Key summary + Import Behavior selector during an
 * import run. Lookup-mode imports get a simplified info panel instead
 * of the behavior selector: lookup keys are always `lookupType + code`
 * and the backend is locked to upsert-by-composite, so exposing the
 * selector there is actively misleading.
 *
 * Extracted from tenant-web's inline `MatchKeyStepContent` and system-web's
 * inline JSX block. System-web had drifted — it was missing the lookup
 * branch entirely, which silently shipped a misleading behavior
 * selector whenever a lookup template was imported. Mounting this
 * shared component fixes that as a side effect.
 *
 * Stays stateless so both containers can drop it into their existing
 * render pipelines without any lifecycle changes.
 */

import type { FieldMapping, ImportBehavior, ImportDataMode } from '@firesquirrel/local-db';

export interface MatchKeyStepContentProps {
  /** Field mappings from the wizard state — only `isMatchKey` rows are summarized. */
  fieldMappings: FieldMapping[];
  /**
   * Canonical data mode from `@firesquirrel/local-db`. Drives the
   * lookup-vs-standard branch. Undefined is treated as 'parcel' for
   * maximum compatibility with hosts that hadn't yet adopted the
   * canonical enum.
   */
  dataMode: ImportDataMode;
  /** Currently selected import behavior. Ignored in lookup mode. */
  importBehavior: ImportBehavior;
  /** Called when the user picks a different behavior. */
  onBehaviorChange: (behavior: ImportBehavior) => void;
}

/**
 * Canonical UI labels for each `ImportBehavior`. Kept co-located with
 * the step so both apps render exactly the same strings. The values
 * are the wire values — never user-facing.
 *
 * Historic note: system-web's legacy container had `'insert-only'` as
 * the wire value here; that was a typo (silenced with a force-cast)
 * that would 400 at commit. Fixed 2026-04-23.
 */
export const BEHAVIOR_OPTIONS: {
  value: ImportBehavior;
  label: string;
  desc: string;
}[] = [
  { value: 'upsert',    label: 'Upsert',      desc: 'Create new, update existing' },
  { value: 'insert',    label: 'Insert Only', desc: 'Skip if match key exists' },
  { value: 'append',    label: 'Append',      desc: 'Always insert new records' },
  { value: 'full-sync', label: 'Full Sync',   desc: 'Upsert + soft-delete absent' },
];

export default function MatchKeyStepContent({
  fieldMappings,
  dataMode,
  importBehavior,
  onBehaviorChange,
}: MatchKeyStepContentProps) {
  const isLookup = dataMode === 'lookup';
  const isParcelOwnerUpdate = dataMode === 'parcelOwnerUpdate';
  const matchKeys = fieldMappings.filter(m => m.isMatchKey);

  // Lookup imports: match key is always lookupType + code, behavior is
  // always upsert-by-composite. No configuration surface — just inform.
  if (isLookup) {
    return (
      <div className="space-y-4" data-testid="match-key-lookup-info">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-xs text-blue-800">
          <div className="font-semibold mb-1">Lookup mode — match key is fixed</div>
          <p>
            Lookup values are always matched by <strong>lookupType + code</strong> composite key.
            No configuration needed.
          </p>
          <p className="mt-1 text-blue-600">
            Import behavior: <strong>Upsert</strong> (create new, update existing by lookupType + code).
          </p>
        </div>
      </div>
    );
  }

  // parcelOwnerUpdate (Phase 5a): update-only, freshness-guarded. Insert /
  // append / full-sync are unreachable for this mode, so the behavior
  // selector is replaced with a fixed info panel — exposing it would be
  // actively misleading (same rationale as the lookup branch).
  if (isParcelOwnerUpdate) {
    return (
      <div className="space-y-4" data-testid="match-key-owner-update-info">
        <p className="text-xs text-gray-500">
          Rows are matched to existing parcels by the match key. Matching parcels get their
          ownership data updated; rows with no match are skipped — this import never creates
          parcels.
        </p>
        {matchKeys.length === 0 ? (
          <div
            className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3"
            data-testid="match-key-empty"
          >
            No match key defined. A match key (e.g. parcel number) is required — go back to
            Map Columns to designate one.
          </div>
        ) : (
          <div className="flex flex-wrap gap-2" data-testid="match-key-list">
            {matchKeys.map(m => (
              <span
                key={m.targetField}
                className="bg-blue-50 border border-blue-200 text-blue-800 text-xs rounded-full px-3 py-1"
              >
                {m.targetField} ← {m.sourceColumns.join(', ')}
              </span>
            ))}
          </div>
        )}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-xs text-blue-800">
          <div className="font-semibold mb-1">Ownership update — update-only</div>
          <p>
            Only <strong>ownership.*</strong> fields are written. Existing parcels whose
            recorded ownership date is newer than this file&apos;s data-as-of date are
            skipped automatically, so a stale file can never overwrite fresher data.
          </p>
          <p className="mt-1 text-blue-600">
            Import behavior: <strong>Update only</strong> (no creates, no deletes).
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="match-key-step">
      <p className="text-xs text-gray-500">
        Records with matching values in these fields will be updated; others will be created.
      </p>

      {matchKeys.length === 0 ? (
        <div
          className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3"
          data-testid="match-key-empty"
        >
          No match keys defined. All records will be inserted as new. Go back to Map Columns to
          designate at least one match key.
        </div>
      ) : (
        <div className="flex flex-wrap gap-2" data-testid="match-key-list">
          {matchKeys.map(m => (
            <span
              key={m.targetField}
              className="bg-blue-50 border border-blue-200 text-blue-800 text-xs rounded-full px-3 py-1"
            >
              {m.targetField} ← {m.sourceColumns.join(', ')}
            </span>
          ))}
        </div>
      )}

      <div>
        <div className="text-xs font-semibold text-gray-700 mb-2">Import Behavior</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {BEHAVIOR_OPTIONS.map(opt => (
            <button
              key={opt.value}
              type="button"
              data-testid={`behavior-${opt.value}`}
              onClick={() => onBehaviorChange(opt.value)}
              className={`border-2 rounded-lg px-3 py-2.5 text-left transition-colors ${
                importBehavior === opt.value
                  ? 'border-orange-500 bg-orange-50'
                  : 'border-gray-200 hover:border-gray-300 bg-white'
              }`}
            >
              <div className="text-xs font-semibold text-gray-800">{opt.label}</div>
              <div className="text-[11px] text-gray-500 mt-0.5">{opt.desc}</div>
            </button>
          ))}
        </div>

        {importBehavior === 'full-sync' && (
          <div className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
            ⚠ Records not present in this file will be soft-deleted from the database.
          </div>
        )}
      </div>
    </div>
  );
}
